import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { FORM_TYPE_CREATE_DATA_SOURCE } from 'js/constants/formTypes';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
  CONNECTOR_CODE_SQL_RUNNER,
  CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
} from 'js/constants/connectionCategories';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { useSqlRunnerFormState } from 'new/hooks/useSqlRunnerFormState';
import { GET_ALL_CREDENTIALS_REQUEST, GET_FULL_VENDOR_REQUEST } from 'js/constants/actionTypes';
import {
  loadFullVendor,
  loadDataCredentials,
  loadDataSource,
  updateFormData,
  setFormStageIndex,
  addDataSourceFromForm,
  updateDataSourceFromForm,
  setContextualSupport,
  unsetContextualSupport,
  probeGetConnectionTree,
  probeGetInitialDBTree,
  probeGetSample,
  probeClearAll,
  setFormError,
  setFormAwaitingValidation,
} from 'js/actions';
import Form from '../../elements/forms/Form';
import FormPage from '../../elements/forms/FormPage';
import UIUtils from 'js/lib/UIUtils';
import { getEmptyRequiredFields, formatValidationErrorMessage } from 'js/lib/ValidationUtils';
import { CONNECTORS_RESOURCE_TYPES } from 'js/constants/resourceTypes';
import AuthenticateDataSource from './AuthenticateDataSource';
import SelectConnection from './SelectConnection';
import ConfigureSqlRunner from './ConfigureSqlRunner';
import {
  getActiveItems,
  getScriptCredentialFieldForStorageType,
  getInitialTemplateId,
  getSqlRunnerEndpointIdFromPaging,
  getSelectStepData,
  getAuthStepConfig,
  STORAGE_TYPE_PARAM,
} from './SqlRunnerSourceForm.helpers';
import type {
  SqlRunnerSourceFormProps,
  FormValidationCallback,
  FormValidationError,
  AuthField,
  ConnAuthByIdEntry,
} from './SqlRunnerSourceForm.types';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useSqlRunnerEditHydration } from './useSqlRunnerEditHydration';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { CustomUserConfirmation } from 'js/components/route_utils/UserConfirmation';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';

const FORM_TYPE = FORM_TYPE_CREATE_DATA_SOURCE;
const CONNECTION = CONNECTOR_CODE_SQL_RUNNER;
const DEFAULT_SOURCE_NAME = CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME;

/**
 * 3-step form for ELT Script Runner: Select DB type → Authenticate → Configure.
 */
function SqlRunnerSourceForm(props: SqlRunnerSourceFormProps): React.ReactElement {
  const { onDismiss, parentDataSetId, flowType = 'streaming', id: sourceId } = props;
  const dispatch = useAppDispatch();
  const state = useSqlRunnerFormState();
  const onConfigureSuccessRef = useRef<FormValidationCallback | null>(null);
  const mountInitDoneRef = useRef(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const pendingSaveRef = useRef<{
    sourceInfo: Record<string, unknown>;
    onSuccess: FormValidationCallback;
    onError: FormValidationError;
  } | null>(null);

  const sourceBeingEdited = useSelector(
    (reduxState: { entities?: { sources?: Record<string, unknown> } }) =>
      sourceId != null && reduxState.entities?.sources
        ? (reduxState.entities.sources[String(sourceId)] as Record<string, unknown> | null)
        : null,
  );

  useSqlRunnerEditHydration(sourceId);

  useEffect(() => {
    return () => {
      dispatch(probeClearAll());
    };
  }, [dispatch]);

  const updateForm = useCallback(
    (data: Record<string, unknown>, resetValidation?: boolean) =>
      dispatch(updateFormData(FORM_TYPE, data, resetValidation)),
    [dispatch],
  );

  const setContextualSupportHandler = useCallback(
    (key: string) => dispatch(setContextualSupport(key)),
    [dispatch],
  );
  const unsetContextualSupportHandler = useCallback(
    (key: string) => dispatch(unsetContextualSupport(key)),
    [dispatch],
  );

  const scriptCredField = useMemo(
    () =>
      getScriptCredentialFieldForStorageType(
        state.paging,
        state.selectedConnection,
        state.templatizedScriptConfig,
      ),
    [state.paging, state.selectedConnection, state.templatizedScriptConfig],
  );

  const effectiveCredentialType =
    (sourceBeingEdited?.source_type as string | undefined) ??
    scriptCredField?.config?.credential_type ??
    scriptCredField?.credential_type ??
    null;

  const authStepConfig = useMemo(
    () => getAuthStepConfig(state.paging, effectiveCredentialType, state.selectedTemplate),
    [state.paging, effectiveCredentialType, state.selectedTemplate],
  );

  const selectStepData = useMemo(() => getSelectStepData(state.paging), [state.paging]);

  const isLoadingFullVendor = useIsLoading(GET_FULL_VENDOR_REQUEST);
  const isLoadingCredentials = useIsLoading(GET_ALL_CREDENTIALS_REQUEST);
  const isLoadingSelectStep = selectStepData.isLoading || isLoadingFullVendor;

  const configureStepFieldsList = useMemo(() => {
    const byId = state.paging?.sourceConfigById as Record<string, unknown[]> | undefined;
    if (!byId) return undefined;
    const sqlRunnerFields = (byId[CONNECTION] ?? []) as { name?: string }[];
    const dbFields = (effectiveCredentialType ? (byId[effectiveCredentialType] ?? []) : []) as {
      name?: string;
    }[];
    const seen = new Set<string>();
    const merged: { name?: string }[] = [];
    [...dbFields, ...sqlRunnerFields].forEach((f) => {
      if (f?.name && !seen.has(f.name)) {
        seen.add(f.name);
        merged.push(f);
      }
    });
    return merged.length ? merged : undefined;
  }, [state.paging?.sourceConfigById, effectiveCredentialType]);

  useEffect(() => {
    if (mountInitDoneRef.current) return;
    mountInitDoneRef.current = true;
    if (sourceId != null) {
      dispatch(
        loadDataSource(sourceId, {
          aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        }),
      );
      dispatch(loadFullVendor('sql_runner'));
      dispatch(loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key));
    } else {
      dispatch(loadFullVendor('sql_runner'));
      dispatch(loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key));

      const initialTemplate = getInitialTemplateId(state.paging);
      updateForm({
        selectedConnection: CONNECTION,
        selectedTemplate: initialTemplate,
      });
      dispatch(setFormStageIndex(FORM_TYPE, 0));
    }
  }, [sourceId]);

  useEffect(() => {
    if (!state.configureAwaitingDataSource || !state.addedDataSource) return;
    if (onConfigureSuccessRef.current) {
      updateForm({ configureAwaitingDataSource: false });
      onConfigureSuccessRef.current();
      onConfigureSuccessRef.current = null;
    }
  }, [state.configureAwaitingDataSource, state.addedDataSource, updateForm]);

  const handleDismiss = useCallback(() => {
    onDismiss?.(state.addedDataSource ?? null);
  }, [onDismiss, state.addedDataSource]);

  const handleScriptConfigChange = useCallback(
    (data: Record<string, unknown>, name?: string, value?: unknown) => {
      let next = { ...state.templatizedScriptConfig, ...data };
      if (name != null) next[name] = value;
      next = UIUtils.configWithMissingDefaults(
        next,
        state.paging?.sourceConfigById?.[state.selectedConnection] as
          | Record<string, unknown>[]
          | undefined,
      ) as Record<string, unknown>;
      updateForm({ templatizedScriptConfig: next });
    },
    [state.paging, state.selectedConnection, state.templatizedScriptConfig, updateForm],
  );

  const handleCredentialInputChange = useCallback(
    (input: Record<string, unknown>, resetValidation = true) => {
      const payload: Record<string, unknown> = { ...input };
      if (input.selectedCredentialId != null && scriptCredField) {
        payload.templatizedScriptConfig = {
          ...state.templatizedScriptConfig,
          [scriptCredField.name]: input.selectedCredentialId,
        };
      }
      updateForm(payload, resetValidation);
    },
    [scriptCredField, state.templatizedScriptConfig, updateForm],
  );

  const onAuthenticateEnter = useCallback(() => {
    const credId =
      scriptCredField &&
      (state.templatizedScriptConfig?.[scriptCredField.name] as number | string | undefined);
    if (scriptCredField && credId) updateForm({ selectedCredentialId: credId }, false);

    const effectiveType = effectiveCredentialType ?? CONNECTION;
    if (effectiveType) {
      dispatch(loadFullVendor(effectiveType))?.then?.(() =>
        dispatch(loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key)),
      );
    }

    const connAuth = effectiveType
      ? (state.paging.connAuthById?.[effectiveType] as ConnAuthByIdEntry | undefined)
      : null;
    const { templates, items } = connAuth ?? {};
    const initialTemplate = getInitialTemplateId(state.paging);
    const fields = getActiveItems(templates, items, initialTemplate);
    updateForm({
      connectionAuthFields: fields ?? [],
      selectedTemplate: initialTemplate,
    });
  }, [
    scriptCredField,
    state.templatizedScriptConfig,
    state.paging,
    effectiveCredentialType,
    dispatch,
    updateForm,
  ]);

  const onSourceConfigChanged = useCallback(
    (data: Record<string, unknown>) =>
      updateForm({
        sourceConfig: { ...data, db_query_mode: 'Query' as const },
      }),
    [updateForm],
  );

  const handleFormDataUpdate = useCallback(
    (formData: Record<string, unknown>) => updateForm(formData),
    [updateForm],
  );

  const onRequestProbeTree = useCallback(
    (isRoot = true, selectedFolder = null) => {
      if (state.selectedCredentialId != null) {
        dispatch(probeGetConnectionTree(state.selectedCredentialId, isRoot, selectedFolder));
      }
    },
    [dispatch, state.selectedCredentialId],
  );

  const onRequestInitialDBProbeTree = useCallback(
    (selectedFolder: unknown) => {
      if (state.selectedCredentialId != null) {
        dispatch(probeGetInitialDBTree(state.selectedCredentialId, true, selectedFolder));
      }
    },
    [dispatch, state.selectedCredentialId],
  );

  const onGetProbeSample = useCallback(() => {
    if (state.selectedCredentialId == null) return;
    const payload = {
      ...state.sourceConfig,
      db_query_mode: 'Query',
    };
    dispatch(probeGetSample(state.selectedCredentialId, 0, payload));
  }, [dispatch, state.selectedCredentialId, state.sourceConfig]);

  const onTableSelected = useCallback(
    (selectedTable: Record<string, unknown> | null, database?: string) => {
      const updates: Record<string, unknown> = {
        selectedTable: selectedTable ?? null,
      };
      if (selectedTable != null) {
        updates.sourceConfig = {
          ...state.sourceConfig,
          table: selectedTable?.table ?? selectedTable?.name,
          database: database ?? state.sourceConfig?.database,
        };
      }
      updateForm(updates);
    },
    [state.sourceConfig, updateForm],
  );

  const onChangeTemplate = useCallback(
    (id: number) => {
      const connAuth = effectiveCredentialType
        ? (state.paging.connAuthById?.[effectiveCredentialType] as ConnAuthByIdEntry | undefined)
        : null;
      const fields = getActiveItems(connAuth?.templates, connAuth?.items, id);
      updateForm({ selectedTemplate: id, connectionAuthFields: fields ?? [] });
    },
    [effectiveCredentialType, state.paging, updateForm],
  );

  const onSelectDatabaseType = useCallback(
    (selectedType: string) => {
      const endpointId = getSqlRunnerEndpointIdFromPaging(state.paging);
      handleScriptConfigChange(
        {
          ...state.templatizedScriptConfig,
          [STORAGE_TYPE_PARAM]: selectedType,
          vendor_endpoint_id: endpointId,
        },
        STORAGE_TYPE_PARAM,
        selectedType,
      );
    },
    [state.paging, state.templatizedScriptConfig, handleScriptConfigChange],
  );

  // Validation: Select step
  const validateSelect = useCallback(
    (_: unknown, onSuccess: FormValidationCallback, onError: FormValidationError) => {
      if (!state.templatizedScriptConfig?.[STORAGE_TYPE_PARAM]) {
        onError('Please select a database type.');
        return;
      }
      onSuccess();
    },
    [state.templatizedScriptConfig],
  );

  // Validation: Authenticate step
  const validateCredential = useCallback(
    (
      data: Record<string, unknown>,
      onSuccess: FormValidationCallback,
      onError: FormValidationError,
    ) => {
      const credId = data.selectedCredentialId;
      const credInfo = data.selectedCredentialInfo as Record<string, unknown> | undefined;
      const isNew = data.isNewCredential;

      const effectiveType =
        (credInfo?._vendorConnectionType as string) ??
        effectiveCredentialType ??
        state.selectedConnection;
      const connAuth = state.paging.connAuthById?.[effectiveType] as ConnAuthByIdEntry | undefined;
      const authFields = getActiveItems(
        connAuth?.templates,
        connAuth?.items,
        state.selectedTemplate,
      ) as AuthField[];

      if (credId == null || credId === '') {
        onError('Please select a credential.');
        return;
      }

      if (isNew) {
        const missing = getEmptyRequiredFields(authFields, credInfo ?? {});
        if (!_.get(credInfo, 'cred_name')) {
          missing.unshift({
            name: 'cred_name',
            display_name: 'Credential Name',
          });
        }
        if (missing.length > 0) {
          onError(formatValidationErrorMessage(missing));
          return;
        }
      }
      onSuccess();
    },
    [
      effectiveCredentialType,
      state.selectedConnection,
      state.selectedTemplate,
      state.paging.connAuthById,
    ],
  );

  // Validation: Configure step (query required, then dispatch addDataSourceFromForm)
  const validateConfigure = useCallback(
    (_: unknown, onSuccess: FormValidationCallback, onError: FormValidationError) => {
      if (state.configureAwaitingDataSource && state.addedDataSource) {
        updateForm({ configureAwaitingDataSource: false });
        onSuccess();
        return;
      }
      const name = (state.sourceName ?? '').trim() || DEFAULT_SOURCE_NAME;
      const query = String(state.sourceConfig?.query ?? null).trim();
      if (!query) {
        onError('Please enter a query to fetch data from the database.');
        return;
      }

      const sourceInfo = UIUtils.buildSqlRunnerSourceFromTemplatizedScriptConfig(
        state.templatizedScriptConfig,
        state.paging?.sourceConfigById?.[state.selectedConnection] as
          | Record<string, unknown>[]
          | undefined,
      );
      if (sourceId != null && state.selectedCredentialId != null) {
        sourceInfo.data_credentials_id = state.selectedCredentialId;
      }
      sourceInfo.name = name;
      sourceInfo.description = (state.sourceDescription ?? '').trim() || undefined;
      sourceInfo.source_config = {
        ...(sourceInfo.source_config || {}),
        ...state.sourceConfig,
        db_query_mode: 'Query',
      };
      if (!sourceInfo.script_config) sourceInfo.script_config = { config: {} };
      if (!sourceInfo.script_config.config) sourceInfo.script_config.config = {};
      sourceInfo.script_config.config['parameters.db_query_mode'] = 'Query';
      sourceInfo.script_config.config['parameters.sql_query'] =
        (state.sourceConfig?.query as string) ?? '';
      const startCron = state.sourceConfig?.['start.cron'] as string | undefined;
      if (startCron) {
        sourceInfo.script_config.config['parameters.script.start.cron'] = startCron;
      }
      const isEdit = sourceId != null;

      if (
        isEdit &&
        sourceBeingEdited?.status === 'ACTIVE' &&
        shouldRenderRunAction(
          sourceBeingEdited as Parameters<typeof shouldRenderRunAction>[0],
          CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
        )
      ) {
        pendingSaveRef.current = { sourceInfo, onSuccess, onError };
        setShowSaveConfirmation(true);
        return;
      }

      onConfigureSuccessRef.current = onSuccess;
      updateForm({ configureAwaitingDataSource: true });

      const promise = isEdit
        ? dispatch(
            updateDataSourceFromForm(
              FORM_TYPE,
              sourceId,
              sourceInfo,
              CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
              false,
              state.selectedCredentialId,
              null,
              null,
              false,
            ),
          )
        : dispatch(
            addDataSourceFromForm(
              FORM_TYPE,
              CONNECTION,
              CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
              sourceInfo,
              state.selectedCredentialId,
              false,
              true,
              false,
              parentDataSetId ?? undefined,
              flowType,
            ),
          );
      if (promise && typeof (promise as Promise<unknown>)?.catch === 'function') {
        (promise as Promise<unknown>).catch(() => {
          updateForm({ configureAwaitingDataSource: false });
          onConfigureSuccessRef.current = null;
          onError(
            isEdit
              ? 'Failed to save source. Please try again.'
              : 'Failed to create source. Please try again.',
          );
        });
      }
    },
    [
      state.configureAwaitingDataSource,
      state.addedDataSource,
      state.sourceName,
      state.sourceConfig,
      state.templatizedScriptConfig,
      state.paging,
      state.selectedConnection,
      state.selectedCredentialId,
      parentDataSetId,
      flowType,
      sourceId,
      sourceBeingEdited,
      dispatch,
      updateForm,
    ],
  );

  const handleSaveConfirmation = useCallback(
    (runWithIngestion: boolean) => {
      const pending = pendingSaveRef.current;
      setShowSaveConfirmation(false);
      pendingSaveRef.current = null;
      if (!pending || sourceId == null) return;

      updateForm({ configureAwaitingDataSource: true });
      const promise = dispatch(
        updateDataSourceFromForm(
          FORM_TYPE,
          sourceId,
          pending.sourceInfo,
          CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
          false,
          state.selectedCredentialId,
          null,
          null,
          runWithIngestion,
        ),
      );
      if (promise && typeof (promise as Promise<unknown>)?.then === 'function') {
        (promise as Promise<unknown>)
          .then(() => {
            updateForm({ configureAwaitingDataSource: false });
            onConfigureSuccessRef.current = null;
            pending.onSuccess();
          })
          .catch(() => {
            updateForm({ configureAwaitingDataSource: false });
            onConfigureSuccessRef.current = null;
            pending.onError('Failed to save source. Please try again.');
          });
      }
    },
    [sourceId, state.selectedCredentialId, dispatch, updateForm],
  );

  const onCloseSaveConfirmation = useCallback(() => {
    setShowSaveConfirmation(false);
    pendingSaveRef.current = null;
    dispatch(setFormAwaitingValidation(FORM_TYPE, false));
  }, [dispatch]);

  const isEditMode = sourceId != null;

  return (
    <>
      <Form
        type={FORM_TYPE}
        title={isEditMode ? 'Edit Automation' : 'New Automation'}
        isModal
        onSubmit={handleDismiss}
        onDismiss={handleDismiss}
        submitButtonText={isEditMode ? 'Save' : 'Create'}
        alwaysShowCancel
      >
        <FormPage
          title="Select"
          validationMethod={validateSelect}
          disabled={isEditMode}
          disabledTooltip="The database type cannot be changed when editing an automation."
        >
          <SelectConnection
            connectorResourceType={CONNECTORS_RESOURCE_TYPES.SOURCE}
            connectionTypes={selectStepData.connectionTypes}
            connectionMetas={selectStepData.connectionMetas}
            connectionsAllowed={selectStepData.connectionsAllowed}
            connectionsComingSoon={[]}
            selectedConnection={
              (state.templatizedScriptConfig?.[STORAGE_TYPE_PARAM] as string) ?? null
            }
            onSelected={onSelectDatabaseType}
            setContextualSupport={setContextualSupportHandler}
            unsetContextualSupport={unsetContextualSupportHandler}
            singleCategoryView
            headerTitle="Select Database Type"
            headerSubtitle="Please choose the type of database you want to connect to."
            isLoadingVendors={isLoadingSelectStep}
            credentialsByType={state.credentialsByType}
          />
        </FormPage>
        <FormPage
          title="Authenticate"
          onEnter={onAuthenticateEnter}
          showBackButton
          validationMethod={validateCredential}
          contentHeight={750}
        >
          <AuthenticateDataSource
            id={1}
            connectionTypes={authStepConfig.connectionTypes}
            connectionMetas={authStepConfig.connectionMetas}
            vendors={state.vendors}
            selectedConnection={state.selectedConnection}
            effectiveCredentialType={effectiveCredentialType ?? undefined}
            selectedTemplate={state.selectedTemplate}
            templates={authStepConfig.templates}
            onChangeTemplate={onChangeTemplate}
            connectionAuthFields={authStepConfig.connectionAuthFields}
            connectionAuthMeta={authStepConfig.connectionAuthMeta}
            dataCredentials={state.credentialsByType}
            credentialsById={state.credentialsById}
            credFormData={{
              selectedCredentialId: state.selectedCredentialId,
              selectedCredentialInfo: state.selectedCredentialInfo ?? undefined,
              isNewCredential: state.isNewCredential,
            }}
            isFormAwaitingValidation={state.isFormAwaitingValidation}
            isLoadingData={isLoadingCredentials}
            onFormInputChanged={handleCredentialInputChange}
            onCredSelectionChanged={() => {}}
            onRefreshClicked={() =>
              dispatch(loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key))
            }
            setContextualSupport={setContextualSupportHandler}
            unsetContextualSupport={unsetContextualSupportHandler}
          />
        </FormPage>
        <FormPage
          title="Configure"
          showBackButton
          nextButtons={[
            {
              label: isEditMode ? 'Save' : 'Create',
              validationMethod: validateConfigure,
            },
          ]}
          contentHeight={750}
        >
          <ConfigureSqlRunner
            sourceName={state.sourceName}
            sourceDescription={state.sourceDescription}
            onSourceNameChange={(v) => updateForm({ sourceName: v })}
            onSourceDescriptionChange={(v) => updateForm({ sourceDescription: v })}
            sourceConfig={state.sourceConfig}
            onSourceConfigChanged={onSourceConfigChanged}
            updateFormData={handleFormDataUpdate}
            selectedCredentialId={state.selectedCredentialId}
            selectedConnection={(effectiveCredentialType as string) ?? state.selectedConnection}
            connectionMetas={(state.paging?.metaInfoById as Record<string, unknown>) ?? {}}
            sourceConfigFields={(state.paging?.sourceConfigById as Record<string, unknown>) ?? {}}
            sourceConfigFieldsList={configureStepFieldsList}
            onRequestProbeTree={onRequestProbeTree}
            onRequestInitialDBProbeTree={onRequestInitialDBProbeTree}
            onGetProbeSample={onGetProbeSample}
            probeData={state.probeData}
            probeDataFlat={state.probeDataFlat}
            probeSample={state.probeSample}
            probeError={state.probeError}
            probeTreeLastUpdatedAt={state.probeTreeLastUpdatedAt}
            isLoadingProbeData={state.isLoadingProbeData}
            isLoadingProbeSample={state.isLoadingProbeSample}
            selectedTable={state.selectedTable}
            onTableSelected={onTableSelected}
            setContextualSupport={setContextualSupportHandler}
            unsetContextualSupport={unsetContextualSupportHandler}
            onError={(msg) => dispatch(setFormError(FORM_TYPE, msg))}
            sourceBeingEdited={sourceBeingEdited ?? null}
          />
        </FormPage>
      </Form>
      {showSaveConfirmation && (
        <CustomUserConfirmation
          title="Save Changes to Active Source"
          message="You've made changes to an active source. Choose how you want to apply these changes:"
          cancelButtonLabel="Cancel"
          yesWithChangesButtonLabel="Save & Run Now"
          okButtonLabel="Save for Next Run"
          onConfirm={(runWithIngestion) => {
            handleSaveConfirmation(runWithIngestion);
          }}
          onClose={onCloseSaveConfirmation}
          onRunWithIngestion={(runWithIngestion) => {
            handleSaveConfirmation(runWithIngestion);
          }}
        />
      )}
    </>
  );
}

export default SqlRunnerSourceForm;
